import { useEffect, useId, useRef, useState } from 'react';
import { MAX_SERVICE_QUESTION_LENGTH, MAX_SERVICE_QUESTIONS } from '@acs/shared';
import { Banner, Button, ConfirmDialog, Field, Icon, IconButton, Input, Modal, Select } from '@acs/ui';
import { useActiveBusiness } from '../../app/ActiveBusinessLayout.js';
import type { ServiceDoc } from '../../shared/api/converters.js';
import type { ServiceEdit } from '../../shared/api/services.js';
import { useI18n } from '../../i18n/index.js';
import { ALL_DAY, durationOptions, durationParts } from './serviceDuration.js';
import {
  checkDraft,
  draftChanged,
  draftOf,
  emptyDraft,
  MAX_SERVICE_PRICE,
  newQuestionRow,
  positiveNumeric,
  type ServiceDraft,
} from './serviceForm.js';

export interface ServiceDialogProps {
  /** null = create a new service. */
  service: ServiceDoc | null;
  pending: boolean;
  saveError: boolean;
  onSubmit(edit: ServiceEdit): void;
  onClose(): void;
}

/**
 * Add or edit one service (doc 64): name · length · price (optionally a "from –
 * to" range) and the questions asked before booking. One dialog for both, in
 * Settings and in the setup wizard, so the two can never offer different
 * fields.
 *
 * It closes only once the save has succeeded (the parent unmounts it); a failed
 * save keeps the draft and says so. While saving nothing can be edited and
 * nothing closes it — Escape included, which the modal hears on its own. A
 * draft with changes asks before it is thrown away.
 */
export function ServiceDialog({ service, pending, saveError, onSubmit, onClose }: ServiceDialogProps) {
  const { t, tag } = useI18n();
  const business = useActiveBusiness();
  const lengthLabel = useDurationLabel();
  const formId = useId();
  const priceId = useId();
  // Built once: question rows get their ids when they are created, never on a render.
  const [initial] = useState<ServiceDraft>(() => (service ? draftOf(service) : emptyDraft()));
  const [draft, setDraft] = useState<ServiceDraft>(initial);
  const [discarding, setDiscarding] = useState(false);
  /** Rows left at least once, and whether a save was tried: an empty row the
   * owner has only just added is not an error yet. */
  const [touched, setTouched] = useState<ReadonlySet<string>>(() => new Set());
  const [attempted, setAttempted] = useState(false);
  const inputs = useRef(new Map<string, HTMLInputElement>());
  const addRef = useRef<HTMLButtonElement>(null);
  /** Where focus goes after the next render: a row's key, or the Add button. */
  const [focusNext, setFocusNext] = useState<string | 'add' | null>(null);

  useEffect(() => {
    if (focusNext === null) return;
    if (focusNext === 'add') addRef.current?.focus();
    else inputs.current.get(focusNext)?.focus();
    setFocusNext(null);
  }, [focusNext]);

  const check = checkDraft(draft);
  const dirty = draftChanged(initial, draft);
  // Adding needs only a valid draft; saving an edit also needs a change.
  const canSubmit = check.edit !== null && (service ? dirty : true) && !pending;
  const atLimit = draft.questions.length >= MAX_SERVICE_QUESTIONS;

  const set = (patch: Partial<ServiceDraft>) => setDraft((d) => ({ ...d, ...patch }));

  /** ✕, Escape, the backdrop and Cancel all come here. */
  const requestClose = () => {
    if (pending) return;
    if (dirty) setDiscarding(true);
    else onClose();
  };

  const addQuestion = () => {
    if (atLimit) return;
    const row = newQuestionRow();
    setDraft((d) => ({ ...d, questions: [...d.questions, row] }));
    setFocusNext(row.key);
  };

  const removeQuestion = (key: string) => {
    const at = draft.questions.findIndex((q) => q.key === key);
    const rest = draft.questions.filter((q) => q.key !== key);
    setDraft((d) => ({ ...d, questions: d.questions.filter((q) => q.key !== key) }));
    // To the row that took its place, else the one before, else Add.
    setFocusNext(rest[at]?.key ?? rest[at - 1]?.key ?? 'add');
  };

  const setQuestion = (key: string, text: string) =>
    setDraft((d) => ({ ...d, questions: d.questions.map((q) => (q.key === key ? { ...q, text } : q)) }));

  const footer = (
    <>
      <Button variant="secondary" onClick={requestClose} disabled={pending}>
        {t('common.cancel')}
      </Button>
      {/* In the footer, outside the form: tied to it by `form`, so Enter in any
          field and this button submit the same way. */}
      <Button type="submit" form={formId} loading={pending} disabled={!canSubmit}>
        {service ? t('common.save') : t('services.addService')}
      </Button>
    </>
  );

  return (
    <Modal
      size="lg"
      closeLabel={t('ui.closeDialog')}
      title={service ? t('services.editTitle') : t('services.addTitle')}
      // Escape and the backdrop are heard by every open overlay: while the
      // discard question is up, they answer only that.
      onClose={discarding ? () => {} : requestClose}
      footer={footer}
    >
      <form
        id={formId}
        className="service-dialog"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          // A portal does not stop React events: never let this submit reach a
          // form the editor happens to sit in.
          e.stopPropagation();
          if (canSubmit && check.edit) onSubmit(check.edit);
          else setAttempted(true);
        }}
      >
        <fieldset className="service-dialog__fields" disabled={pending}>
          <div className="service-form">
            <Field label={t('services.service')} className="service-form__name">
              <Input
                autoFocus
                placeholder={t('services.placeholder')}
                value={draft.name}
                maxLength={80}
                onChange={(e) => set({ name: e.target.value })}
              />
            </Field>
            {/* A select, not a minutes box (doc 45): 5 min … 8 h, then all day. */}
            <Field label={t('services.duration')} className="service-form__length">
              <Select
                value={String(draft.duration)}
                onChange={(e) => set({ duration: e.target.value === ALL_DAY ? ALL_DAY : Number(e.target.value) })}
                options={durationOptions(draft.duration).map((d) => ({
                  value: String(d),
                  label: d === ALL_DAY ? t('services.allDay') : lengthLabel(d),
                }))}
              />
            </Field>
            {/* The currency comes from the business's country, so it is shown,
                not asked for. The second box is the optional top of a range:
                left empty, the price is one fixed figure. */}
            <Field
              label={t('services.price', { currency: business.currency })}
              className="service-form__price"
              htmlFor={priceId}
            >
              <div className="service-form__range">
                <Input
                  id={priceId}
                  inputMode="decimal"
                  placeholder={t('services.priceFrom')}
                  invalid={check.priceTooHigh}
                  value={draft.price}
                  onChange={(e) => set({ price: positiveNumeric(e.target.value, true) })}
                />
                <span aria-hidden>–</span>
                <Input
                  inputMode="decimal"
                  placeholder={t('services.priceTo')}
                  aria-label={t('services.priceToLabel', { currency: business.currency })}
                  invalid={check.rangeInvalid || check.priceTooHigh}
                  value={draft.priceMax}
                  onChange={(e) => set({ priceMax: positiveNumeric(e.target.value, true) })}
                />
              </div>
            </Field>
            {(check.rangeInvalid || check.priceTooHigh) && (
              <div className="field-error service-form__error" role="alert">
                {check.rangeInvalid
                  ? t('services.priceRangeInvalid')
                  : t('services.priceTooHigh', { max: new Intl.NumberFormat(tag).format(MAX_SERVICE_PRICE) })}
              </div>
            )}
          </div>

          <section className="service-questions" aria-labelledby={`${formId}-questions`}>
            <div className="service-questions__head">
              <h3 id={`${formId}-questions`} className="service-questions__title">
                {t('services.questions.title')}
              </h3>
              <span className="muted service-questions__count" aria-live="polite">
                {t('services.questions.count', { count: draft.questions.length, max: MAX_SERVICE_QUESTIONS })}
              </span>
            </div>
            <p className="field-hint service-questions__hint">{t('services.questions.hint')}</p>
            {draft.questions.length > 0 && (
              <ol className="service-questions__list">
                {draft.questions.map((row, i) => {
                  const problem = check.questionProblems[i];
                  const showBlank = problem === 'blank' && (attempted || touched.has(row.key));
                  return (
                    <li key={row.key} className="service-questions__row">
                      <Field
                        label={t('services.questions.label', { n: i + 1 })}
                        className="service-questions__field"
                        error={
                          showBlank
                            ? t('services.questions.blank')
                            : problem === 'tooLong'
                              ? t('services.questions.tooLong', { max: MAX_SERVICE_QUESTION_LENGTH })
                              : undefined
                        }
                      >
                        <Input
                          ref={(el) => {
                            if (el) inputs.current.set(row.key, el);
                            else inputs.current.delete(row.key);
                          }}
                          value={row.text}
                          placeholder={t('services.questions.placeholder')}
                          onChange={(e) => setQuestion(row.key, e.target.value)}
                          onBlur={() => setTouched((s) => (s.has(row.key) ? s : new Set(s).add(row.key)))}
                        />
                      </Field>
                      <IconButton
                        size="sm"
                        variant="danger"
                        className="service-questions__remove"
                        aria-label={t('services.questions.remove', { n: i + 1 })}
                        icon={<Icon name="trash" size={16} />}
                        onClick={() => removeQuestion(row.key)}
                      />
                    </li>
                  );
                })}
              </ol>
            )}
            <div className="service-questions__add">
              <Button
                ref={addRef}
                variant="secondary"
                size="sm"
                leadingIcon={<Icon name="plus" size={16} />}
                disabled={atLimit}
                onClick={addQuestion}
              >
                {t('services.questions.add')}
              </Button>
              {atLimit && (
                <span className="muted service-questions__limit">
                  {t('services.questions.limit', { max: MAX_SERVICE_QUESTIONS })}
                </span>
              )}
            </div>
          </section>
        </fieldset>
        {saveError && <Banner tone="danger">{t('services.saveFailed')}</Banner>}
      </form>

      {discarding && (
        <ConfirmDialog
          tone="warning"
          title={t('services.discardTitle')}
          description={t('services.discardDetail')}
          icon={<Icon name="alert-triangle" size={24} />}
          confirmLabel={t('common.discard')}
          cancelLabel={t('bookingForm.keepEditing')}
          onCancel={() => setDiscarding(false)}
          onConfirm={onClose}
        />
      )}
    </Modal>
  );
}

/** "45 min", "1 h", "1 h 30 min" in the owner's language. */
export function useDurationLabel(): (min: number) => string {
  const { t } = useI18n();
  return (min: number) => {
    const { h, m } = durationParts(min);
    if (h === 0) return t('services.minutesShort', { count: m });
    return m === 0 ? t('services.durationH', { h }) : t('services.durationHM', { h, m });
  };
}
